import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

import type { Proceso } from "./tipos"

type Area = { id: number; nombre: string; codigo: string | null; activo: boolean }
type DocumentoSGI = { id: number; tipo: string; codigo: string; titulo: string }

/**
 * El área dueña del proceso y su procedimiento escrito en la Biblioteca.
 *
 * ⚠ **Sin permiso para leer la lista, el dato se dice como texto.** Un selector vacío
 * —o uno que falla con 403— parecería que el proceso no tiene área; quien sólo ve los
 * procesos lee el nombre que ya trae el propio proceso.
 *
 * ⚠ **Sólo se ofrecen PROCEDIMIENTOS.** Atar un proceso a un manual o a una política
 * contestaría «¿dónde está escrito cómo se hace?» con un documento que no lo dice; el
 * servidor lo rechaza igual.
 */
export function VinculosDelProceso({
  proceso,
  onCambiar,
}: {
  proceso: Proceso
  onCambiar: (cambios: Partial<Proceso>) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const verAreas = tienePermiso(usuario, "departamento:ver") || tienePermiso(usuario, "cargo:ver")
  const verBiblioteca = tienePermiso(usuario, "biblioteca:ver")

  const areas = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<Area[]>("/api/v1/departamentos"),
    enabled: verAreas,
  })
  const documentos = useQuery({
    queryKey: ["biblioteca", "documentos"],
    queryFn: () => api.get<DocumentoSGI[]>("/api/v1/biblioteca/documentos"),
    enabled: verBiblioteca,
  })

  const elegiblesArea = (areas.data ?? []).filter(
    (a) => a.activo || a.id === proceso.departamento_id,
  )
  const procedimientos = (documentos.data ?? []).filter((d) => d.tipo === "PRO")

  return (
    <>
      <div>
        <label htmlFor="pr-area" className="mb-1 block text-xs font-medium uppercase">
          Área dueña
        </label>
        {verAreas ? (
          <select
            id="pr-area"
            className="campo"
            value={proceso.departamento_id ?? ""}
            onChange={(e) =>
              onCambiar({
                departamento_id: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          >
            <option value="">Sin área</option>
            {elegiblesArea.map((a) => (
              <option key={a.id} value={a.id}>
                {a.codigo ? `${a.codigo} · ` : ""}
                {a.nombre}
              </option>
            ))}
          </select>
        ) : (
          <p id="pr-area" className="text-sm">
            {proceso.departamento ?? "Sin área"}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="pr-documento" className="mb-1 block text-xs font-medium uppercase">
          Procedimiento escrito (PRO)
        </label>
        {verBiblioteca ? (
          <select
            id="pr-documento"
            className="campo"
            value={proceso.documento_id ?? ""}
            onChange={(e) =>
              onCambiar({ documento_id: e.target.value === "" ? null : Number(e.target.value) })
            }
          >
            <option value="">Sin documento</option>
            {procedimientos.map((d) => (
              <option key={d.id} value={d.id}>
                {d.codigo} · {d.titulo}
              </option>
            ))}
          </select>
        ) : (
          <p id="pr-documento" className="text-sm">
            {proceso.documento ?? "Sin documento"}
          </p>
        )}
      </div>
    </>
  )
}
